// frontend/src/pages/builder/chartTypes.js
// Phase 3 step 7a: which chart types the current fields can draw. Pure and
// unit-tested. Every type that can't be used gets a reason in plain words,
// shown in the builder, so a greyed-out button is never a mystery.
//
// The rules follow what the server returns (app/semantic/embed_render.py):
// with a Group by field the chart has one series per group of the FIRST
// value only, and a KPI shows the first value only. A combination that would
// silently drop a value is not offered; the table always shows everything.
import { MAX_DIMENSIONS, defaultType } from './builderSpec';
import { matrixReason } from './matrixSlots';

/** The v1 chart types (plan decision D4), in switcher order. */
export const CHART_TYPES = [
  { id: 'bar', label: 'Bar' },
  { id: 'stackedBar', label: 'Stacked bar' },
  { id: 'horizontalBar', label: 'Horizontal bar' },
  { id: 'line', label: 'Line' },
  { id: 'area', label: 'Area' },
  { id: 'doughnut', label: 'Donut' },
  { id: 'card', label: 'KPI' },
  { id: 'table', label: 'Table' },
  { id: 'matrix', label: 'Matrix' },      // 7e-3 (decision D12)
];

const IDS = CHART_TYPES.map((t) => t.id);

const NEEDS_X = 'Needs a field on the X axis.';
const ONE_VALUE_WITH_GROUP = 'With a Group by field a chart shows one value. Remove a value or the Group by field.';
const TOO_WIDE = `A chart can group by up to ${MAX_DIMENSIONS} fields. Keep the table, or remove some grouping columns.`;

/** null when the draft can be drawn as this type, otherwise why not. */
export function unavailableReason(id, draft) {
  const dims = draft.dimensions.length;
  const values = Math.max(draft.measures.length, 1);     // no value chosen = count of records
  const hasX = dims >= 1;
  const grouped = dims >= 2;

  if (id === 'table') return null;
  if (!IDS.includes(id)) return 'Unknown chart type.';
  // A matrix: 1 to 5 row fields and up to 2 column fields (7e-3).
  if (id === 'matrix') return matrixReason(draft);
  // 7d-2: a table may have up to 20 grouping columns; a chart can draw three
  // (X plus two Group by). Nothing is trimmed behind the user's back.
  if (dims > MAX_DIMENSIONS) return TOO_WIDE;

  switch (id) {
    case 'card':
      if (hasX) return 'A KPI is a single number. Remove the X axis and Group by fields.';
      if (values > 1) return 'A KPI shows one value. Remove the extra values.';
      return null;
    case 'doughnut':
      if (!hasX) return 'A donut needs a field on the X axis for its slices.';
      if (grouped) return "A donut can't use Group by. Remove the Group by field.";
      if (values > 1) return 'A donut shows one value. Remove the extra values.';
      return null;
    case 'stackedBar':
      if (!hasX) return NEEDS_X;
      if (grouped && values > 1) return ONE_VALUE_WITH_GROUP;
      if (!grouped && values < 2) return 'Stacked bars need a Group by field, or two or more values, to stack.';
      return null;
    case 'bar':
    case 'horizontalBar':
    case 'line':
    case 'area':
      if (!hasX) return NEEDS_X;
      if (grouped && values > 1) return ONE_VALUE_WITH_GROUP;
      return null;
    default:
      return 'Unknown chart type.';
  }
}

/** { [typeId]: reason | null } for every v1 type. */
export function availability(draft) {
  return Object.fromEntries(IDS.map((id) => [id, unavailableReason(id, draft)]));
}

/**
 * The type to draw: the user's choice while the fields allow it, otherwise
 * the suggested type (KPI, line for a date on X, else bar), otherwise the
 * table. The choice is kept, so it comes back as soon as it fits again.
 */
export function resolveType(chosen, draft, fieldsByKey = {}) {
  if (IDS.includes(chosen) && unavailableReason(chosen, draft) === null) return chosen;
  const suggested = defaultType(draft, fieldsByKey);
  return unavailableReason(suggested, draft) === null ? suggested : 'table';
}